import {
  DueDisplayOptions,
  PlannedOccurrence,
  Task,
  Timestamp,
  WeekDays,
} from '@griply/types';
import {
  getActiveSchedule,
  getScheduleTimeframeByDate,
  getTaskPlanning,
} from '@griply/utils';
import { addDays, endOfDay, isAfter, startOfDay } from 'date-fns';
import { useMemo } from 'react';

export type UseFutureOccurrenceOptions = {
  plannedOccurrence?: PlannedOccurrence;
  displayDue: DueDisplayOptions[];
  today: Timestamp;
  displayDate?: Timestamp;
  weekStartsOn: WeekDays;
};

export const useFutureOccurrence = (
  task: Task,
  {
    plannedOccurrence,
    displayDue,
    today,
    displayDate,
    weekStartsOn,
  }: UseFutureOccurrenceOptions,
) =>
  useMemo(() => {
    if (plannedOccurrence || !displayDue.includes(DueDisplayOptions.Future)) {
      return undefined;
    }
    const dateToMeasureFrom = endOfDay(displayDate ?? today);

    if (
      task.endStrategy?.deadline &&
      isAfter(task.endStrategy.deadline, endOfDay(dateToMeasureFrom))
    ) {
      return getTaskPlanning(task, {
        startDate: startOfDay(dateToMeasureFrom),
        endDate: dateToMeasureFrom,
        today,
        weekStartsOn,
      })[0];
    }

    const activeSchedule = getActiveSchedule(task.schedules, today);
    if (!activeSchedule) {
      return undefined;
    }

    const timeframe = getScheduleTimeframeByDate(
      activeSchedule,
      dateToMeasureFrom,
      weekStartsOn,
    );
    const planning = getTaskPlanning(task, {
      startDate: dateToMeasureFrom,
      endDate: timeframe.endDate,
      today,
      weekStartsOn,
      uncompletedOnly: true,
    });
    const futureOccurrence = planning.find(({ date }) =>
      isAfter(date, dateToMeasureFrom),
    );
    if (futureOccurrence) {
      return futureOccurrence;
    }

    const nextTimeframe = getScheduleTimeframeByDate(
      activeSchedule,
      addDays(timeframe.endDate, 1),
      weekStartsOn,
    );
    const nextPlanning = getTaskPlanning(task, {
      startDate: nextTimeframe.startDate,
      endDate: nextTimeframe.endDate,
      today,
      weekStartsOn,
      uncompletedOnly: true,
    });
    return nextPlanning.find(
      ({ date }) =>
        date.getTime() === nextTimeframe.startDate.getTime() ||
        isAfter(date, nextTimeframe.startDate),
    );
  }, [displayDate, displayDue, plannedOccurrence, task, today, weekStartsOn]);
